/* eslint-disable no-console */
import {
  sentryMonitoringFeature,
  sentryDsn,
  sentryAuthToken,
  sentryEnvironment,
  sentryReplaySessionSampleRate,
  sentryReplayErrorSampleRate,
  isFeatureEnabled,
} from "utils/featureFlags";

/**
 * Sentry Monitoring Service with Feature Flag Support
 *
 * This service is controlled via the `NEPH_FF_SENTRY_MONITORING` feature flag.
 * When disabled, all Sentry operations are no-ops and fallback to console logging.
 *
 * Sentry is lazy-loaded only when enabled to reduce initial bundle size.
 *
 * PHI strategy: BAA in place makes Sentry a covered HIPAA processor. Masking and
 * scrubbing are both DENY-BY-DEFAULT (NKF-1090):
 *
 *   - Replay DOM: every text node is masked unless an ancestor opts out via the
 *     replayIntegration unmask list.
 *   - Captured payloads (network bodies, event.extra): a value is dropped unless its
 *     key appears in SAFE_KEYS below.
 *   - event.contexts is the exception, and is allowlisted by block name instead - see
 *     SDK_CONTEXTS. Its contents are SDK protocol fields, not application payload, so
 *     the payload allowlist only destroyed them.
 *
 * This replaced a denylist of the 18 HIPAA direct identifiers, which failed three
 * ways: it missed prefixed variants (the encounter serializer emits
 * patient_first_name, never listed), it could not cover the ~20 nested routes that
 * /api/patients/ matches, and it could not touch free-text clinical narrative
 * (soap_notes, plan_note, smart_note_data) where identifiers live inside the string
 * rather than in the key. An allowlist fails closed on all three: a field nobody
 * anticipated is redacted, not leaked.
 *
 * Adding a key to SAFE_KEYS is a deliberate, reviewable act. The cost of forgetting
 * one is a "[REDACTED]" in a debugging session, not a HIPAA incident.
 */

type UserContext = {
  id?: string | number | null;
  email?: string | null;
  username?: string | null;
  [key: string]: any;
};

type SentryModuleType = typeof import("@sentry/react");

// Derived from the SDK rather than imported, so the lazy-loaded module stays lazy.
type Breadcrumb = Parameters<SentryModuleType["addBreadcrumb"]>[0];
type SentryEvent = NonNullable<Parameters<SentryModuleType["captureEvent"]>[0]>;

let isInitialized = false;
let isEnabled = false;
let isInitializing = false;
let SentryModule: SentryModuleType | null = null;

// ALLOWLIST. A value survives only if its key is listed here; everything else becomes
// "[REDACTED]". Grounded in the DRF serializers behind the networkDetailAllowUrls
// endpoints, so the entries are real field names, not guesses.
//
// The guiding line: identifiers, state, and coded values are safe; names and free text
// are not. Note the deliberate omissions - bare "name" is NOT listed even though a lab
// test name is harmless, because the same key is a patient name elsewhere and a flat
// allowlist cannot tell them apart. Use the unambiguous variants (drug_name,
// normalized_name) instead. If a collision like this ever needs resolving, add
// path-scoping rather than loosening the key.
//
// NEEDS CLINICAL REVIEW: the vitals measures and lab values below are clinical data,
// which monitoring policy keeps visible - but height/weight are mild quasi-identifiers.
// Trim if compliance wants a narrower cut.
const SAFE_KEYS = new Set([
  // Client-side debugging signal. actionType is a Redux action type ("Patient/...Failure")
  // emitted by the middleware in services/logger.ts, which is the only thing that makes
  // those breadcrumbs and error events legible. It is schema, not patient data. Deliberately
  // not joined by "error": an action.error message can echo a server response, and there is
  // no way to tell one that quotes submitted values from one that does not.
  "actionType",

  // Plumbing, pagination, and opaque foreign keys.
  "id",
  "url",
  "count",
  "next",
  "previous",
  "page",
  "page_size",
  "patient_id",
  "location_id",
  "user_id",
  "account_id",
  "encounter_id",
  "appointment_id",
  "document_id",
  "prescription_id",
  "lab_test_id",
  "duplicate_id",
  "dispensable_drug_id",
  "fhir_resource_id",
  // Opaque foreign keys emitted in the WS breadcrumbs (useWebSocketHooks). Same class as the
  // *_id keys above - identifiers, not PHI - and dropping them guts the encounter-processing
  // and smart-summary breadcrumb trail.
  "encounter_recording_id",
  "process_id",

  // State machines and processing status - the encounter-flow debugging signal.
  "status",
  "state",
  "etl_state",
  "patient_status",
  "ckd_status",
  "normalized_status",
  "outcome",
  "visit_type",
  "ehr_note_type",
  "sign_close_ind",
  "user_rejected",
  "manually_updated",
  "suspect_ocr",
  "suspect_ocr_reason",
  "source",
  "etl_error",
  "normalization_error_data",
  // Smart-summary / encounter WS processing progress (useWebSocketHooks). Schema describing
  // where a background job is, not patient data. "history" arrays of {status, duration_ms}
  // survive because their leaf keys are allowlisted here.
  "current_step",
  "duration_ms",
  // End-visit flow timing (services/endVisitTiming). Elapsed milliseconds, a stepper position,
  // and how the provider entered the flow. Schema and clock readings, no clinical content.
  // step_name and step_index were already being sent by the stepper's step-transition breadcrumb
  // and arriving as "[REDACTED]" because neither was listed here.
  "step_name",
  "step_index",
  "active_ms",
  "generation_ms",
  "ready_ms",
  "entry_point",
  // Deliberately NOT allowlisted: "updated_value" (ws.pif). The key name is generic enough
  // that it could carry a submitted field value, so it fails closed like any unknown key.

  // Timestamps. Dates of service are not dates of birth; DOB keys are absent by design.
  "created_at",
  "updated_at",
  "etl_last_updated",
  "last_emr_data_fetch",
  "datetime",
  "date_time",
  "start_date",
  "end_date",
  "visit_date",
  "end_datetime",
  "onset",
  "deleted_at",
  "smart_summary_last_updated",

  // Coded clinical values - codes, not narrative.
  "code",
  "ckdcode",
  "icd_codes",
  "cpt_codes",
  "loinc_number",
  "loinc_short_name",
  "stage",
  "severity",
  "drug_name",
  "normalized_name",
  "med_form",
  "dosage_quantity_value",
  "dosage_quantity_unit",
  "dosage_period",
  "dosage_period_unit",
  "refills_allowed",
  "dispense_quantity",
  "frequency",
  "bound_period_start",
  "bound_period_end",

  // Lab and vital measurements with their units.
  "value",
  "unit",
  "reference_range",
  "normalized_value",
  "normalized_unit",
  "normalized_reference_range",
  "height",
  "height_unit",
  "weight",
  "weight_unit",
  "systolic",
  "diastolic",
  "systolic_unit",
  "diastolic_unit",
  "bmi",
  "bmi_unit",
  "temperature",
  "temperature_unit",
  "pulse",
  "pulse_unit",
  "heart_rate",
  "heart_rate_unit",
  "respiration_rate",
  "respiration_rate_unit",
  "spo2",
  "spo2_unit",
  "fiO2",
  "fiO2_unit",
  "bsa",
  "bsa_unit",
  "glucose_level",
  "glucose_unit",
  "urine_output",
]);

/**
 * Recursively redacts every leaf value whose key is not in SAFE_KEYS.
 *
 * Structure and key names are always preserved - the shape of a payload is the useful
 * part for debugging, and a key name is schema, not patient data. Only leaf values are
 * judged, so containers are always walked rather than redacted wholesale: an allowlisted
 * key holding an object (say "value" wrapping a nested blob) does not blanket-approve
 * everything beneath it.
 *
 * Booleans are always kept. A boolean cannot carry an identifier, and dropping flags
 * like has_rpm_vitals would gut the debugging signal for no privacy gain.
 *
 * @param key the key that holds `obj`; undefined at the root. Array elements inherit
 *            the array's own key, so icd_codes: ["N18.3"] survives as a unit.
 */
export function scrubObject(obj: unknown, key?: string): unknown {
  if (Array.isArray(obj)) {
    return obj.map((item) => scrubObject(item, key));
  }
  if (obj !== null && typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = scrubObject(v, k);
    }
    return out;
  }
  // Leaf value: keep it only if something vouched for its key.
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === "boolean") return obj;
  if (key !== undefined && SAFE_KEYS.has(key)) {
    // A "url" leaf is allowlisted for its debugging value, but the URL itself can carry
    // identifiers in its path (a patient id, or the token+email in /createNewPassword).
    // Run it through the same scrubber the breadcrumb and replay-resource paths already use
    // so extra/contexts do not become the one channel that leaks them.
    return key === "url" && typeof obj === "string" ? scrubUrl(obj) : obj;
  }
  return "[REDACTED]";
}

/**
 * Context blocks whose contents are written by the SDK and by Sentry's own ingest, not by us.
 *
 * SAFE_KEYS is an allowlist of application payload keys, so running it over event.contexts
 * redacted the protocol itself: trace_id and span_id became the string "[REDACTED]", which
 * fails Sentry's 32-hex validation and shows up in the UI as "Not a valid trace id". The same
 * pass took out replay_id (breaking error-to-replay linking) and every browser/os/runtime/react
 * version. None of that is patient data and none of it was ever ours to redact.
 *
 * Allowlisting by block name rather than by key is deliberate. A per-key list would re-break
 * every time Sentry adds a protocol field (trace.links, browser.brands), reintroducing exactly
 * this bug; a block name is stable. The failure mode stays safe: a context name that is not
 * listed here - including anything a future setContext() call introduces - is still scrubbed
 * leaf by leaf.
 *
 * Deliberately absent: "response", which can carry headers. See scrubContexts for trace.data.
 */
const SDK_CONTEXTS = new Set(["trace", "browser", "os", "device", "runtime", "react", "culture", "app", "replay"]);

/**
 * Passes SDK-owned context blocks through untouched and scrubs everything else.
 *
 * trace.data is the one exception inside a passed-through block: it holds span attributes,
 * which app code can set, so it stays subject to the allowlist even though the protocol fields
 * around it do not.
 *
 * Note what is NOT passed to scrubObject: the block's own name. A context is normally an object,
 * for which the key argument is ignored anyway - but setContext() can be handed a primitive, and
 * a block named after an allowlisted key ("status", "id", "url" are all in SAFE_KEYS) would then
 * have vouched for itself. Omitting the key means an unrecognised context block fails closed
 * whatever its shape.
 */
export function scrubContexts(contexts: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, block] of Object.entries(contexts)) {
    if (!SDK_CONTEXTS.has(name)) {
      out[name] = scrubObject(block);
    } else if (name === "trace" && block !== null && typeof block === "object" && "data" in block) {
      const trace = block as Record<string, unknown>;
      out[name] = { ...trace, data: scrubObject(trace.data, "data") };
    } else {
      out[name] = block;
    }
  }
  return out;
}

/**
 * Reduces a JSON request/response body to its allowlisted fields.
 *
 * A body that is not parseable JSON is redacted wholesale rather than passed through.
 * It used to pass through, which was the single largest hole in the old denylist: a
 * text/html or text/plain response (rendered end-note HTML, a plain-text narrative, a
 * Django error page echoing submitted values) reached Sentry verbatim. There is no way
 * to allowlist inside an opaque blob, so deny-by-default means dropping it.
 */
export function scrubNetworkBody(body: unknown): unknown {
  if (!body) return body;
  try {
    const parsed = typeof body === "string" ? JSON.parse(body) : body;
    const scrubbed = scrubObject(parsed);
    return typeof body === "string" ? JSON.stringify(scrubbed) : scrubbed;
  } catch {
    return "[REDACTED non-JSON body]";
  }
}

/**
 * Redacts credentials carried in a URL path.
 *
 * The /createNewPassword/:hiddenToken/:email route is redacted wholesale - it carries a
 * password reset token and an email address in the path itself. That is a credential, not an
 * identifier, and it stays redacted regardless of anything below.
 *
 * Numeric path segments are deliberately NOT replaced. They used to become [ID], which cost
 * the most useful detail in a request or navigation breadcrumb - which patient's record the
 * provider was actually on - in order to mask an internal surrogate key that identifies nobody
 * outside our own database. Sentry is covered by a BAA, so that trade was all cost and no
 * benefit. It was also incoherent: only path segments were ever rewritten, so /patients/603/
 * was masked while ?patient_ids=603 went through untouched. Keeping ids in both is consistent
 * and debuggable.
 *
 * If a route ever puts something genuinely identifying in the path - an MRN, a name, a DOB -
 * that is a redaction to add here explicitly, not a reason to bring back blanket ID masking.
 *
 * /patients/12345/ -> /patients/12345/
 * /createNewPassword/abc123/a@b.com -> /createNewPassword/[REDACTED]
 */
export function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  return url.replace(/\/createNewPassword\/.*/i, "/createNewPassword/[REDACTED]");
}

/**
 * Applies the deny-by-default rule to a breadcrumb. Wired in as the SDK's beforeBreadcrumb
 * hook, so it sees every breadcrumb, including the ones the SDK generates itself.
 *
 * Breadcrumbs ride along on every captured event, and nothing else covers them: beforeSend
 * only reaches extra and contexts, and replay masking only touches the DOM.
 *
 * Returns null to drop the breadcrumb entirely.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // Console breadcrumbs are dropped outright. The SDK records data.arguments as the raw
  // argument objects and message as those arguments joined into a string, so a single
  // console.log(patient) lands verbatim. This is the same class of problem as a non-JSON
  // response body: uncontrolled free text with no key to allowlist against, so the only
  // safe answer is not to send it. Note NA-6955 removed the previous setupConsoleScrubbing()
  // because it monkey-patched console globally; dropping the breadcrumb achieves the same
  // end without touching console.
  if (breadcrumb.category === "console") return null;

  // Request and navigation breadcrumbs are the highest-value debugging signal, and their
  // shapes are known and narrow (url/method/status_code, and from/to route paths), so they
  // are kept rather than run through the allowlist - status_code, from and to are all
  // unallowlisted keys that would otherwise be blanked, taking the request and route trail
  // with them. Only the paths need work, to strip identifiers out.
  if (breadcrumb.category === "xhr" || breadcrumb.category === "fetch" || breadcrumb.category === "navigation") {
    for (const key of ["url", "from", "to"]) {
      if (typeof breadcrumb.data?.[key] === "string") {
        breadcrumb.data[key] = scrubUrl(breadcrumb.data[key] as string);
      }
    }
    return breadcrumb;
  }

  if (breadcrumb.data) {
    breadcrumb.data = scrubObject(breadcrumb.data) as Record<string, unknown>;
  }

  return breadcrumb;
}

/**
 * Emits a Sentry breadcrumb. No-op when Sentry is not initialized.
 *
 * `data` is not scrubbed here: the SDK runs beforeBreadcrumb (scrubBreadcrumb above) inside
 * its own addBreadcrumb, so manually added breadcrumbs go through the same gate as the
 * SDK-generated ones. This matters most for the Redux middleware in services/logger.ts,
 * which forwards a raw `action.payload` here for any *Error/*FAILURE action - for a patient
 * or encounter thunk that is an unmodified API response.
 */
export function addBreadcrumb(breadcrumb: {
  category: string;
  message?: string;
  data?: Record<string, unknown>;
  level?: "debug" | "info" | "warning" | "error";
}): void {
  if (!isInitialized || !isEnabled || !SentryModule) return;
  try {
    SentryModule.addBreadcrumb(breadcrumb);
  } catch {
    // never throw from monitoring
  }
}

/**
 * True for the hostnames a developer's own machine serves the UI on. Exported so index.tsx
 * and the replay gate below agree on what "local" means instead of each keeping a list.
 */
export function isLocalHostname(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "0.0.0.0" || hostname.endsWith(".local");
}

/**
 * Localhost opt-in for running Sentry at all, set as VITE_SENTRY_LOCAL=true in
 * webapp/ui/.env.local. Requires a dev-server restart, which is the point: turning your local
 * session into Sentry traffic should be a deliberate act, not a checkbox you forget is on.
 *
 * This replaces the `if (true || !isLocalHost)` edit that local verification used to require.
 * That edit was one stray commit away from pointing every developer's localhost at Sentry.
 *
 * VITE_SENTRY_REPLAY implies this. Replay cannot record without the SDK initialised, so an
 * opt-in to replay that did not also opt in to Sentry would be a silent no-op - the failure
 * mode of a flag you set, restart for, and then watch do nothing.
 */
export function isLocalSentryOptIn(): boolean {
  return import.meta.env?.VITE_SENTRY_LOCAL === "true" || isLocalReplayOptIn();
}

/**
 * Localhost opt-in for session replay specifically, set as VITE_SENTRY_REPLAY=true.
 *
 * Split from isLocalSentryOptIn because replay is the expensive half. Errors, transactions and
 * logs and metrics (smart_summary.refresh, encounter.lifecycle) cost their own quota;
 * replays bill against a 50/month organization allowance. Before the split the only switch
 * that started Sentry locally was this one, so verifying anything else - user attribution, a
 * refresh event, a scrub - meant burning replays to look at something that was not a replay.
 */
export function isLocalReplayOptIn(): boolean {
  return import.meta.env?.VITE_SENTRY_REPLAY === "true";
}

/**
 * Resolves a replay sample rate from AppConfig. DENY-BY-DEFAULT, matching the masking and
 * scrubbing posture above: an absent, disabled, or unparseable flag yields 0 rather than
 * falling back to a hardcoded rate.
 *
 * These rates used to be literals - 1.0 for every non-production environment plus 1.0 on
 * error everywhere - which spent the plan's 50 replays/month on dev and stage browsing and
 * on errored sessions, leaving nothing for the production incidents replay exists for.
 * Failing closed means a forgotten AppConfig entry costs visibility, not the quota.
 *
 * Setting the flag's `enabled` to false is the kill switch: it forces the rate to 0 without
 * anyone having to edit the numeric attribute.
 *
 * `enabled` must be explicitly true. Today the backend cannot produce an object-shaped flag
 * without it - _load_aws_flags only builds one when 'enabled' in value - but requiring it here
 * means the deny-by-default guarantee is enforced by this function rather than resting on that
 * invariant holding forever.
 */
export function resolveReplaySampleRate(featureFlags: Record<string, any> | null | undefined, flag: string): number {
  const flagData = featureFlags?.[flag];
  if (!flagData || typeof flagData !== "object" || flagData.enabled !== true) {
    return 0;
  }

  const raw = flagData.attributes?.[flag.toLowerCase()];
  const parsed = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() !== "" ? Number(raw) : NaN;

  // Out-of-range values are rejected, not clamped. A 5 in AppConfig is a typo, and clamping
  // it to 1.0 would silently record every session - the exact failure this ticket fixes.
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return 0;
  }

  return parsed;
}

/**
 * The SDK's beforeSend hook, as a named export so it can be tested.
 *
 * It lived inline in init() until NKF-1090's contexts regression, which the unit tests could
 * not have caught: they exercise scrubObject in isolation, and every one of them passed while
 * the composition - that same scrub applied to event.contexts - was destroying the trace and
 * replay ids. The hook has to be reachable from a test for the composition to be asserted.
 *
 * Note what "error event" means here: the SDK gates beforeSend on the event having no `type`,
 * not on its level, so this also runs for any info-level captureEvent. As of NKF-1355 there are
 * none left - every measurement moved to metrics and logs, which have their own beforeSendMetric
 * and beforeSendLog hooks - so in practice this now sees only genuine errors. The level-splitting
 * of user email below is kept anyway rather than simplified away: it is the rule scrubEvent and
 * beforeSendLog share, and an info-level captureEvent added later must not silently lose it.
 * Transactions are what beforeSend never sees - they carry a type - which is why user email
 * survives on ui.screen spans (NKF-1355).
 */
export function scrubEvent<T extends SentryEvent>(event: T): T {
  // Strip auth headers
  if (event.request?.headers) {
    delete event.request.headers.cookie;
    delete event.request.headers.authorization;
    delete event.request.headers["x-api-key"];
  }

  // id and email at every level; username never.
  //
  // This used to strip errors to a numeric id, citing "no PII in error reports". The premise
  // does not hold: only clinical staff can log in - Patient has no FK to the user model and
  // there is no patient portal - so user.email is a workforce member's work email, not PHI.
  // And the split protected nothing, because the info branch already sent the same address to
  // the same BAA-covered vendor; it only made the error channel, the one that matters during
  // an incident, worse than the analytics channel. Same incoherence as masking an id in a URL
  // path while letting the query string through.
  //
  // username is still dropped: it adds nothing over email.
  if (event.user) {
    event.user = { id: event.user.id, email: event.user.email };
  }

  // Reduce developer-supplied context to allowlisted keys. This is stricter than
  // it was: a debug field added at a captureException call site is redacted until
  // its key is added to SAFE_KEYS. That is the intended trade - unrecognised keys
  // fail closed - but it does mean error context is thinner than before.
  if (event.extra) {
    event.extra = scrubObject(event.extra) as typeof event.extra;
  }

  // Contexts are SDK-owned metadata on a fixed schema, so they get block-level treatment
  // rather than the payload allowlist. See SDK_CONTEXTS.
  if (event.contexts) {
    event.contexts = scrubContexts(event.contexts) as typeof event.contexts;
  }

  // The explicit password/token/secret sweep that used to sit here is gone: those
  // keys are not in SAFE_KEYS, so the allowlist already redacted them above.

  return event;
}

/**
 * Attribute values Sentry Logs can index. Anything else - an object, an array, an Error - has
 * to be flattened by the caller into fields it actually wants to query on, which is the point:
 * a log you cannot filter by is a console.log with extra steps.
 */
export type LogAttributes = Record<string, string | number | boolean | undefined>;

/**
 * Console-sourced logs are tagged with this origin by the SDK's ConsoleLogs integration.
 */
const SENTRY_ORIGIN_ATTRIBUTE = "sentry.origin";
const CONSOLE_LOG_ORIGIN = "auto.console.logging";

/**
 * Attribute namespaces the SDK owns, not the application.
 *
 * logs/internal.js populates these on every log BEFORE beforeSendLog is called:
 * sentry.release, sentry.environment, sentry.sdk.*, sentry.origin, sentry.replay_id,
 * sentry.trace.parent_span_id, sentry.message.template / .parameter.N, and the
 * user.id / user.email / user.name triple read off the scope.
 *
 * They are protocol fields on a fixed schema, so they get block-level treatment rather than
 * the payload allowlist - the same reasoning, and the same mistake already made once, as
 * event.contexts and SDK_CONTEXTS. Running SAFE_KEYS over them does not make anything safer;
 * it just destroys them, because no SDK key is in SAFE_KEYS and none ever will be.
 */
// Shared by logs and metrics: the SDK stamps its own attributes under this prefix on both
// channels, before our beforeSend hooks run.
const SDK_ATTRIBUTE_PREFIX = "sentry.";

/**
 * Splits telemetry attributes into SDK-owned, which pass through untouched, and caller payload,
 * which goes through the deny-by-default allowlist.
 *
 * Shared by beforeSendLog and beforeSendMetric because the two hooks got this wrong
 * independently. The logs version shipped redacting every SDK attribute - environment,
 * sentry.release, sentry.sdk.*, user.id - across 34 consecutive logs, and the metrics hook
 * reintroduced exactly that the moment it was written separately. The SDK enriches both channels
 * with the same attribute set before our hook runs (see _enrichMetricAttributes and its logs
 * equivalent), so there is one rule here and one place to get it right.
 */
function scrubTelemetryAttributes(attributes: Record<string, unknown>): Record<string, unknown> {
  const sdkOwned: Record<string, unknown> = {};
  const payload: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(attributes)) {
    if (key.startsWith(SDK_ATTRIBUTE_PREFIX)) {
      sdkOwned[key] = value;
    } else if (key === "user.id") {
      sdkOwned[key] = value;
    } else if (key === "user.email") {
      // Kept at every level, matching scrubEvent. See the reasoning there: this is staff
      // email, not PHI, and level-splitting it only degraded the channel that matters.
      sdkOwned[key] = value;
    } else if (key === "user.name") {
      // Never kept, matching scrubEvent's treatment of event.user.username.
    } else {
      payload[key] = value;
    }
  }

  return { ...sdkOwned, ...(scrubObject(payload) as Record<string, unknown>) };
}

/**
 * The logs pipeline's filter, and the reason it exists.
 *
 * Logs are their own signal in the SDK: beforeSend and beforeBreadcrumb never see them
 * (logs/internal.js reads only enableLogs and beforeSendLog), so without this hook the
 * SAFE_KEYS allowlist that governs every other channel simply does not apply here.
 *
 * Four rules:
 *
 *   1. Console-sourced logs are dropped unless they are errors. The integration's `levels`
 *      option already does this, but that is a config array anyone can widen in a debugging
 *      session, and widening it is not a safe edit: console.log("updatedDiagnosis", diagnosis)
 *      exists today in EndVisitDetail, and the integration folds its arguments into the message
 *      STRING (formatConsoleArgs, depth 3) as well as into attributes. Rule 3 cleans the
 *      attributes; nothing can clean a pre-serialized string. So the policy is enforced here,
 *      where it cannot be widened by accident, and the config array is merely the fast path.
 *
 *   2. SDK-owned attributes pass through untouched, and the user identity follows the same
 *      policy scrubEvent applies to event.user: id and email survive at every level, the
 *      username never does. Without this the logs channel
 *      loses the very attribution it exists for - `sentry.environment` came back [REDACTED]
 *      on every log in a live run, and `user.id` would have gone the same way, which would
 *      have quietly negated the identity fix that motivated logging session expiry at all.
 *
 *   3. Everything else is application payload and goes through scrubObject, exactly as
 *      event.extra does. An attribute key nobody allowlisted is redacted, not leaked.
 *
 *   4. A log whose body is not a string is dropped rather than guessed at. Note this does NOT
 *      catch the console case - formatConsoleArgs always returns a string - which is precisely
 *      why rule 1 exists rather than relying on this one.
 *
 * The primary control remains that first-party logs pass structured attributes instead of
 * interpolating objects into the message. This is the floor that makes it a guarantee.
 */
export function beforeSendLog<T extends { level?: unknown; message?: unknown; attributes?: Record<string, unknown> }>(log: T): T | null {
  try {
    if (typeof log.message !== "string") return null;

    if (log.attributes?.[SENTRY_ORIGIN_ATTRIBUTE] === CONSOLE_LOG_ORIGIN && log.level !== "error") {
      return null;
    }

    if (log.attributes) {
      log.attributes = scrubTelemetryAttributes(log.attributes);
    }
    return log;
  } catch {
    // A throw here would lose the log silently anyway; dropping it is the honest outcome.
    return null;
  }
}

/**
 * Scrubs metric attributes, the same deny-by-default allowlist beforeSendLog applies to logs.
 *
 * Defence in depth rather than the only line of defence. The emitters below pass a closed set of
 * enum-like strings - a step name, an entry point, an outcome - and deliberately no identifiers,
 * so the payload is safe by construction even if this hook is wired to the wrong option key on a
 * future SDK. Metric attributes are also a cardinality budget, not a debugging channel: an
 * encounter or patient id here would mint a new series per encounter. Those live on the log line
 * instead, which is where per-encounter drill-down belongs.
 */
export function beforeSendMetric<T extends { name?: unknown; attributes?: Record<string, unknown> }>(metric: T): T | null {
  try {
    if (typeof metric.name !== "string") return null;
    if (metric.attributes) {
      metric.attributes = scrubTelemetryAttributes(metric.attributes);
    }
    return metric;
  } catch {
    return null;
  }
}

/**
 * Resolves the DSN, letting a localhost run send to its own Sentry project instead of the
 * shared one NEPH_FF_SENTRY_DSN names. A local UI proxies /api to the DEV backend, so without
 * an override every local verification run lands in `nephrolytics-ui` next to real dev traffic
 * and has to be filtered back out by environment.
 *
 * Deliberately not a general fallback: off localhost the AppConfig DSN is the only one
 * considered, so a VITE_SENTRY_DSN that somehow reaches a deployed build cannot redirect
 * that environment's events into a personal project.
 *
 * Note this isolates events, not spend. Replay and event quota are billed per organization,
 * and a personal project in the same org draws on the same allowance.
 */
export function resolveDsn(featureFlags: Record<string, any> | null | undefined, hostname: string | null | undefined): string | null {
  const flagDsn = featureFlags?.[sentryDsn]?.attributes?.[sentryDsn.toLowerCase()] ?? null;

  if (!isLocalHostname(hostname)) {
    return flagDsn;
  }

  const override = import.meta.env?.VITE_SENTRY_DSN;
  return typeof override === "string" && override.trim() !== "" ? override.trim() : flagDsn;
}

export async function initMonitoring({ featureFlags }: { featureFlags: Record<string, any> }): Promise<void> {
  if (isInitialized || isInitializing) {
    return;
  }

  const sentryEnabled = featureFlags ? isFeatureEnabled(featureFlags, sentryMonitoringFeature) : false;

  if (!sentryEnabled) {
    isInitialized = true;
    return;
  }

  isInitializing = true;
  isEnabled = true;

  const getAttValue = (flag: string) => {
    const flagData = featureFlags?.[flag];
    if (flagData?.attributes) {
      const attrKey = flag?.toLowerCase();
      return flagData.attributes[attrKey];
    }
    return null;
  };

  // Read once: the hostname gates both which project events go to and how they are sampled.
  const hostname = typeof window !== "undefined" ? window.location?.hostname : null;
  const onLocalhost = isLocalHostname(hostname);

  const dsn = resolveDsn(featureFlags, hostname);
  const authToken = getAttValue(sentryAuthToken);
  // VITE_SENTRY_ENVIRONMENT lets a local run tag its sessions with its own environment
  // (e.g. "local-evan") so they are separable from the shared dev environment.
  const environment = import.meta.env?.VITE_SENTRY_ENVIRONMENT || getAttValue(sentryEnvironment) || "development";
  const release = import.meta.env?.VITE_APP_VERSION || "1.0.0";

  if (!dsn) {
    isInitialized = true;
    return;
  }

  try {
    SentryModule = await import("@sentry/react");
  } catch (error) {
    console.error("Failed to load Sentry module:", error);
    isInitializing = false;
    return;
  }

  // Replay sampling comes from AppConfig so a quota burn can be throttled without a frontend
  // deploy. Localhost is decided from the hostname rather than inherited: a local UI proxies to
  // the DEV backend, so without this it would pick up DEV's rates and bill local browsing against
  // the shared 50-replay allowance. Opting in forces full capture - a session you deliberately
  // recorded for masking verification is useless if it was sampled away.
  // Replay is opted into separately from Sentry itself: reaching this line on localhost only
  // means VITE_SENTRY_LOCAL was set, which must not start recording sessions.
  const localSentryOptIn = onLocalhost && isLocalSentryOptIn();
  const localReplayOptIn = onLocalhost && isLocalReplayOptIn();

  let replaySessionSampleRate: number;
  let replaysOnErrorSampleRate: number;
  if (onLocalhost) {
    replaySessionSampleRate = localReplayOptIn ? 1.0 : 0;
    replaysOnErrorSampleRate = localReplayOptIn ? 1.0 : 0;
  } else {
    replaySessionSampleRate = resolveReplaySampleRate(featureFlags, sentryReplaySessionSampleRate);
    replaysOnErrorSampleRate = resolveReplaySampleRate(featureFlags, sentryReplayErrorSampleRate);
  }

  // Both rates at 0 means replay is off. Skip the integration entirely rather than installing
  // the rrweb recorder and sampling everything out of it.
  const replayEnabled = replaySessionSampleRate > 0 || replaysOnErrorSampleRate > 0;

  const tracesSampleRate = environment === "production" ? 0.1 : 1.0;

  try {
    SentryModule.init({
      dsn,
      environment,
      release,
      ...(authToken && { authToken }),
      sendDefaultPii: false,
      tracesSampleRate,
      tracePropagationTargets: ["localhost", /^https:\/\/.*\.nephrolytics\.ai\/api/, /^https:\/\/dev\.lab\.nephrolytics\.ai/],
      replaysSessionSampleRate: replaySessionSampleRate,
      replaysOnErrorSampleRate: replaysOnErrorSampleRate,
      enableLogs: true,
      debug: environment !== "production",
      beforeSend: scrubEvent,
      beforeBreadcrumb: scrubBreadcrumb,
      beforeSendLog,
      // Application Metrics, needing @sentry/react >= 10.25.0 - 10.21 had the API in
      // @sentry/core but @sentry/browser did not re-export it. Both options are top level as of
      // 10.25; the _experiments.enableMetrics / _experiments.beforeSendMetric forms this was
      // first written against are deprecated there. enableMetrics already defaults to true, but
      // it is set explicitly because "we send metrics" is a decision worth reading in the config
      // rather than inferring from an SDK default that could change.
      enableMetrics: true,
      // Cast because the SDK's own types and docs disagree: the option is typed
      // `(metric: Metric) => Metric` while its docstring says "Returning `null` will cause the
      // metric to be dropped". The runtime honours null, and dropping a malformed metric is the
      // behaviour we want, so the prose is taken as authoritative over the signature.
      beforeSendMetric: beforeSendMetric as (metric: unknown) => never,
      allowUrls: [/^https:\/\/.*\.nephrolytics\.ai/, /^https:\/\/dev\.lab\.nephrolytics\.ai/, /^http:\/\/localhost/],
      integrations: (integrations) => {
        return [
          ...integrations,
          SentryModule.browserTracingIntegration(),
          // Omitted entirely when both sample rates resolve to 0 - see replayEnabled above.
          ...(!replayEnabled
            ? []
            : [
                SentryModule.replayIntegration({
                  // EXPERIMENT (NKF-1090): masking inverted to mask-by-default. Every text node
                  // and the title/placeholder/aria-label attributes are blanked unless an ancestor
                  // matches the unmask list below. Forgetting an annotation now costs readability
                  // instead of leaking PHI.
                  //
                  // The unmask list is deliberately empty of real selectors for this first run:
                  // the point is to inventory what actually becomes unreadable, then promote only
                  // PHI-free chrome and status regions by tagging them data-replay-safe. Resist
                  // unmasking whole panels (Smart Summary, KCF) - a broad unmask ancestor makes any
                  // PHI inside it visible again unless a nearer data-sentry-mask wins.
                  //
                  // data-sentry-mask annotations still matter: they win over a further unmask
                  // ancestor, and attribute masking only engages while maskAllText is true.
                  maskAllText: true,
                  unmask: ["[data-replay-safe]", ".replay-safe"],
                  maskAllInputs: true,
                  blockAllMedia: true,
                  // Capture request/response bodies for encounter-flow endpoints
                  networkDetailAllowUrls: [
                    /\/api\/encounter-recordings\//,
                    /\/api\/patients\//,
                    /\/api\/orders\//,
                    /\/api\/smart-notes\//,
                    /\/api\/encounter-notes\//,
                  ],
                  // Scrub HIPAA identifiers from captured network bodies and URLs before buffering.
                  // Note: beforeSend does NOT fire for replay network events — this hook is required.
                  beforeAddRecordingEvent(event) {
                    const payload = (event as any)?.data?.payload;
                    if (!payload) return event;
                    if (payload.op !== "resource.fetch" && payload.op !== "resource.xhr") return event;

                    if (payload.description) {
                      payload.description = scrubUrl(payload.description);
                    }

                    const reqBody = payload?.data?.request?.body;
                    if (reqBody) {
                      payload.data.request.body = scrubNetworkBody(reqBody);
                    }

                    const resBody = payload?.data?.response?.body;
                    if (resBody) {
                      payload.data.response.body = scrubNetworkBody(resBody);
                    }

                    return event;
                  },
                }),
              ]),
          // Errors only. "warn" used to be here and was 84% of the entire prod logs volume -
          // all of it one message, the session-expiry warning, which is now emitted as a
          // first-party log with a queryable `source` attribute instead of a formatted string.
          // What is left is a safety net: console.error from code nobody has instrumented yet,
          // as unqueryable text, which is better than losing it. Do not add "log" here without
          // reading beforeSendLog first - console arguments are serialized to depth 3.
          SentryModule.consoleLoggingIntegration({ levels: ["error"] }),
        ];
      },
    });

    isInitialized = true;

    // Console handle for local verification - inspecting the scope, forcing a capture, or
    // flushing a replay when one is being recorded:
    //   await window.__sentry.getReplay().flush()
    // Gated on the localhost Sentry opt-in rather than the replay one, since it is just as
    // useful for checking user attribution or a refresh event. Still localhost-only, which is
    // what makes it committable - it cannot appear in a deployed build, so there is no
    // DO-NOT-COMMIT edit to forget.
    if (localSentryOptIn && typeof window !== "undefined") {
      (window as any).__sentry = SentryModule;
    }
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
  } finally {
    isInitializing = false;
  }
}

/**
 * Coerces the user id to a string, which is not cosmetic.
 *
 * Sentry treats user.id as a reserved STRING field. auth.userId is an integer, and the SDK
 * accepts it, puts it in the envelope, and then the logs dataset never surfaces it - measured
 * live: one log arrived carrying user.email and a custom `source` attribute, both queryable,
 * while user.id was simply absent, and the same field HAD surfaced earlier when its value
 * happened to be the string "[REDACTED]". Same field, same pipeline: string appears, integer
 * vanishes.
 *
 * Error events tolerate the number, so this looked fine everywhere except the one place it
 * mattered - it would have cost the "which provider" half of auth.session_expired while the
 * feature otherwise appeared to work.
 */
export function normalizeUserForSentry(user: UserContext | null): UserContext | null {
  if (!user) return null;
  return { ...user, id: user.id == null ? undefined : String(user.id) };
}

export function setUser(user: UserContext | null): void {
  if (!isInitialized || !isEnabled || !SentryModule) return;
  try {
    SentryModule.setUser(normalizeUserForSentry(user));
  } catch (error) {
    console.error("Failed to set Sentry user:", error);
  }
}

/**
 * Sets a Sentry tag. Pass undefined to remove one.
 *
 * Tags exist here for the account id. It used to travel as the Sentry user id, which made
 * count_unique(user) a count of practices rather than people (NKF-1355); as a tag it stays
 * filterable and groupable without pretending to be an identity.
 */
export function setTag(key: string, value: string | number | undefined): void {
  if (!isInitialized || !isEnabled || !SentryModule) return;
  try {
    SentryModule.setTag(key, value);
  } catch {
    // never throw from monitoring
  }
}

export function captureException(error: any, context?: Record<string, any>): void {
  if (!isInitialized || !isEnabled || !SentryModule) {
    return;
  }
  try {
    SentryModule.captureException(error, { extra: context });
  } catch (err) {
    console.error("Failed to capture exception:", err);
  }

  if (import.meta.env?.MODE === "development") {
    console.error("Captured exception:", error, context);
  }
}

export function captureMessage(message: string, context?: Record<string, any>): void {
  if (!isInitialized || !isEnabled || !SentryModule) {
    return;
  }
  try {
    SentryModule.captureMessage(message, { level: "error", extra: context });
  } catch (error) {
    console.error("Failed to capture message:", error);
  }
}

export function isSentryEnabled(): boolean {
  return isInitialized && isEnabled;
}

/**
 * Counts an encounter lifecycle action. Read in Sentry under Explore > Metrics as
 * encounter.lifecycle, grouped by the `state` dimension to get opened against closed - the ratio
 * being the point, since an encounter opened and never closed is the interesting case.
 *
 * The dimension is `state` and not the more obvious `type` because beforeSendMetric applies
 * SAFE_KEYS to dimensions: `type` is not allowlisted, so it arrived as "[REDACTED]" and collapsed
 * opened and closed into one indistinguishable series. `state` is already allowlisted and is the
 * accurate word for it. Allowlisting `type` instead was the wrong fix - it is generic enough to
 * carry a document type or a diagnosis type elsewhere, which is exactly why it fails closed.
 *
 * One metric with a dimension rather than encounter.opened and encounter.closed as separate
 * metrics: two series that have to be charted together to mean anything belong to one metric.
 *
 * A counter rather than a distribution because there is no duration here, and metrics rather
 * than the captureEvent this used until NKF-1355, which made every encounter open and close an
 * ISSUE in the errors dataset. No accompanying log, unlike the other measurements on this file:
 * the Redux middleware calls this with nothing but the action type, so a log line would carry
 * only what the metric's own dimension already carries, and Sentry's Samples view already shows
 * each increment with its trace id and user attribution.
 *
 * Called from the Redux logger middleware on encounterSuccess / closeEncounterSuccess.
 */
export function emitEncounterLifecycle(type: "opened" | "closed"): void {
  emitMetricCount("encounter.lifecycle", { state: type });
}

/**
 * Terminal states of a smart-summary refresh, mirroring CompletedRefresh["status"] in
 * usePatientDataRefresh. Kept as a local union rather than imported so services/ does not
 * depend on hooks/, but it has to stay in step with it: "unknown" means the refresh outran the
 * polling ceiling, which is explicitly not a failure, and lumping it in with "error" or
 * "timeout" would rebuild the false-failure conflation the hook now avoids.
 */
export type RefreshStatus = "success" | "error" | "timeout" | "unknown";

/**
 * Records how long a smart-summary refresh took, from the kickoff POST to a terminal state.
 *
 * Metric for the aggregate - p75 refresh time, grouped by status, which is the number that says
 * whether the pipeline is getting slower. Log for the individual, carrying process_id so one slow
 * refresh can be chased down. process_id is deliberately not a metric dimension: it is unique per
 * refresh and would mint a metric series per run.
 *
 * Both channels are governed by neither tracesSampleRate (0.1 in prod) nor the replay sample
 * rates, so every refresh the tab observes to completion is recorded. That was already the reason
 * this avoided spans.
 *
 * NOT captureEvent, which this used until NKF-1355: an info-level captureEvent lands in the errors
 * dataset and therefore becomes an ISSUE, with a priority, an assignee and a resolve workflow. A
 * refresh duration is not a defect. Same reasoning as the end-visit timing in
 * services/endVisitTiming.
 *
 * Log attribute keys must be in SAFE_KEYS - beforeSendLog runs them through scrubObject, so an
 * unlisted key arrives as "[REDACTED]". The camelCase names on CompletedRefresh (duration,
 * processId) are NOT allowlisted; do not rename these to match the caller.
 *
 * Called from cleanupPatientState in usePatientDataRefresh, which is the single funnel for both
 * completion paths: the websocket "finished" message and polling finding no active processes.
 */
export function emitRefreshMeasurement(status: RefreshStatus, durationMs: number, processId?: string): void {
  emitMetricDistribution("smart_summary.refresh.duration", durationMs, { status });

  emitLog("info", "smart_summary.refresh", {
    status,
    duration_ms: durationMs,
    process_id: processId,
  });
}

/**
 * Records a duration in a Sentry Application Metrics distribution, which is what gives you a real
 * p50/p75/p95 rather than an aggregate over stringified tag values.
 *
 * Guarded on the API being present: Sentry.metrics landed in @sentry/core earlier than
 * @sentry/browser re-exported it, and it requires @sentry/react >= 10.25.0. On an older SDK this
 * no-ops and the log line below still carries the measurement, so nothing is lost by shipping
 * this ahead of the bump. Same defensive shape as startScreenTracking's startInactiveSpan check.
 *
 * Attributes must stay low cardinality - see beforeSendMetric. Identifiers go on the log, not here.
 */
function emitMetricDistribution(name: string, valueMs: number, attributes: Record<string, string>): void {
  if (!isInitialized || !isEnabled || !SentryModule) return;
  try {
    const metrics = (SentryModule as any).metrics;
    if (typeof metrics?.distribution !== "function") return;
    metrics.distribution(name, valueMs, { unit: "millisecond", attributes });
  } catch {
    // never throw from monitoring
  }
}

/**
 * Increments a Sentry Application Metrics counter. Same availability guard as
 * emitMetricDistribution - see there for why the call is defensive.
 *
 * A counter, not a distribution: counting occurrences of something that has no duration. Sentry's
 * Samples view still exposes each individual increment with its trace id and user, so a counter
 * does not lose the per-occurrence detail a distribution would have given.
 */
function emitMetricCount(name: string, attributes: Record<string, string>, value = 1): void {
  if (!isInitialized || !isEnabled || !SentryModule) return;
  try {
    const metrics = (SentryModule as any).metrics;
    if (typeof metrics?.count !== "function") return;
    metrics.count(name, value, { attributes });
  } catch {
    // never throw from monitoring
  }
}

/**
 * Emits one dwell-time measurement for a screen in the end-visit flow, on two channels that
 * answer two different questions.
 *
 * A METRIC for the aggregate - "how long are people on the interim screen" as a percentile over
 * everyone, grouped by screen. This is the number that was asked for, and metrics.distribution is
 * the purpose-built instrument for it.
 *
 * A LOG for the individual - which encounter, which recording, so a surprising p95 can be traced
 * back to a specific visit. Logs carry the identifiers that would blow up metric cardinality.
 *
 * Both channels are governed by neither tracesSampleRate (0.1 in prod) nor the replay rates, so
 * every close the tab observes is recorded rather than one in ten.
 *
 * NOT captureEvent, which an earlier cut of this used: a captured event becomes an ISSUE, and a
 * dwell time is not a defect. It arrived in the Feed with a priority, an assignee and a resolve
 * workflow, and would have accrued an issue group per screen forever.
 *
 * Every log attribute key must be in SAFE_KEYS - beforeSendLog runs them through scrubObject, so
 * an unlisted key arrives as "[REDACTED]" and the measurement is silently lost. Do not rename
 * these to camelCase to match the caller.
 */
export function emitEndVisitPhaseEvent(measurement: {
  phase: string;
  durationMs: number;
  activeMs: number;
  readyMs?: number;
  entry: string;
  encounterRecordingId?: string;
  patientId?: string;
}): void {
  const dimensions = { step_name: measurement.phase, entry_point: measurement.entry };
  emitMetricDistribution("end_visit.screen.duration", measurement.durationMs, dimensions);
  // Wall clock counts a tab left open overnight; active time does not. Both are recorded because
  // the gap between them is itself the interesting signal on the end note screen.
  emitMetricDistribution("end_visit.screen.active_duration", measurement.activeMs, dimensions);
  // Time with real content on screen, which for the interim screen is the difference between
  // "how long was this open" and "how long did they spend reviewing". The interim screen opens on
  // a spinner while the note generates, so its duration is a wait plus a review. Emitted as its
  // own distribution rather than left to be subtracted from the others: generation_ms is measured
  // from the End Visit click while the screen clock starts after the upload, so the two overlap
  // by an unmeasured amount, and subtracting percentiles is not arithmetic anyway.
  if (measurement.readyMs !== undefined) {
    emitMetricDistribution("end_visit.screen.ready_duration", measurement.readyMs, dimensions);
  }

  emitLog("info", "end_visit.phase", {
    step_name: measurement.phase,
    duration_ms: measurement.durationMs,
    active_ms: measurement.activeMs,
    ready_ms: measurement.readyMs,
    entry_point: measurement.entry,
    encounter_recording_id: measurement.encounterRecordingId,
    patient_id: measurement.patientId,
  });
}

/**
 * Emits the End Visit click -> Accept measurement: how long it takes to get the structured
 * assessment agreed, which is the half of the flow our own pipeline is mostly responsible for.
 *
 * Its own distribution rather than something to be derived. The two published series that span
 * the same window - flow.generation_duration and screen.ready_duration - do add up exactly per
 * visit, since one ends at the instant the other begins, but adding their percentiles does not:
 * the provider who waited longest for the note is rarely the one who reviewed longest. They also
 * have different populations, since ready_duration is emitted for deep-linked entries that have
 * no generation reading at all.
 *
 * Pairs with flow.duration - the difference between the two is time spent on the note itself.
 */
export function emitEndVisitAcceptEvent(measurement: {
  entry: string;
  durationMs: number;
  generationMs?: number;
  encounterRecordingId?: string;
  patientId?: string;
}): void {
  const dimensions = { entry_point: measurement.entry };
  emitMetricDistribution("end_visit.flow.accept_duration", measurement.durationMs, dimensions);

  emitLog("info", "end_visit.accept", {
    entry_point: measurement.entry,
    duration_ms: measurement.durationMs,
    generation_ms: measurement.generationMs,
    encounter_recording_id: measurement.encounterRecordingId,
    patient_id: measurement.patientId,
  });
}

/**
 * Emits the end-to-end measurement: the "End Visit" click through to "Sign Notes & End Visit".
 *
 * generation_ms is the machine half - upload plus note generation, before the provider can do
 * anything. It is emitted as its own distribution so the provider half can be read by subtracting
 * one percentile from the other. Unsplit, this number mostly tracks AI latency while looking like
 * it tracks user behaviour.
 */
export function emitEndVisitFlowEvent(measurement: {
  outcome: string;
  entry: string;
  durationMs: number;
  generationMs?: number;
  encounterRecordingId?: string;
  patientId?: string;
}): void {
  const dimensions = { outcome: measurement.outcome, entry_point: measurement.entry };
  emitMetricDistribution("end_visit.flow.duration", measurement.durationMs, dimensions);
  if (measurement.generationMs !== undefined) {
    emitMetricDistribution("end_visit.flow.generation_duration", measurement.generationMs, dimensions);
  }

  emitLog("info", "end_visit.flow", {
    outcome: measurement.outcome,
    entry_point: measurement.entry,
    duration_ms: measurement.durationMs,
    generation_ms: measurement.generationMs,
    encounter_recording_id: measurement.encounterRecordingId,
    patient_id: measurement.patientId,
  });
}

/**
 * Emits the patient intake form measurement: how long the form was open, how much of that the
 * provider was actually looking at it, and how it ended.
 *
 * The outcome rides on the duration dimensions rather than being left to the counter alone. An
 * abandoned form is often a four-second glance, and mixing those into the same distribution as
 * real form fills drags the percentile down until it describes neither - "how long does the PIF
 * take" is only answerable per outcome.
 *
 * pif.completion is a counter on the same call so a counted outcome always has a duration behind
 * it; the two cannot drift apart into disagreeing totals.
 *
 * Every log attribute key must be in SAFE_KEYS - beforeSendLog runs them through scrubObject, so
 * an unlisted key arrives as "[REDACTED]" and the measurement is silently lost. The PIF date is
 * deliberately absent: pif_date is not allowlisted, and the log line's own timestamp plus
 * patient_id is enough to find the visit.
 */
export function emitPifFormEvent(measurement: {
  outcome: string;
  entry: string;
  durationMs: number;
  activeMs: number;
  patientId?: string;
}): void {
  const dimensions = { outcome: measurement.outcome, entry_point: measurement.entry };
  emitMetricDistribution("pif.duration", measurement.durationMs, dimensions);
  // Wall clock counts a form left open over a lunch break; active time does not. The PIF sits
  // open across interruptions far more than the end-visit screens do, so this pair matters more
  // here than it does there.
  emitMetricDistribution("pif.active_duration", measurement.activeMs, dimensions);
  emitMetricCount("pif.completion", { outcome: measurement.outcome });

  emitLog("info", "pif.form", {
    outcome: measurement.outcome,
    entry_point: measurement.entry,
    duration_ms: measurement.durationMs,
    active_ms: measurement.activeMs,
    patient_id: measurement.patientId,
  });
}

/**
 * Emits one section's share of a PIF visit - the answer to "which section costs the time".
 *
 * step_name, not section_name: step_name is already in SAFE_KEYS and section_name is not, so the
 * readable-looking name would arrive as "[REDACTED]" on a chart that still looked healthy. Same
 * key the end-visit screens group by, which also means one grouping convention across both flows.
 *
 * These are ACTIVE ms and they partition the visit: exactly one section is dominant at a time, so
 * summing them for a visit should track pif.active_duration. That reconciliation is the check on
 * the instrumentation - if the two diverge, the dominance switching is losing time.
 */
export function emitPifSectionEvent(measurement: { section: string; durationMs: number; outcome: string; patientId?: string }): void {
  emitMetricDistribution("pif.section.duration", measurement.durationMs, { step_name: measurement.section });

  emitLog("info", "pif.section", {
    step_name: measurement.section,
    duration_ms: measurement.durationMs,
    outcome: measurement.outcome,
    patient_id: measurement.patientId,
  });
}

/**
 * Starts a screen dwell-time span in the CURRENT scope so it inherits the
 * logged-in user context. Returns a cleanup function that ends the span.
 * Call on screen enter; call the returned function on screen exit.
 * Appears in Sentry Performance under op:"ui.screen" with user.id attached,
 * queryable in custom dashboards grouped by span.description + user.id.
 */
export function startScreenTracking(screenName: string): () => void {
  if (!isInitialized || !isEnabled || !SentryModule) return () => {};
  try {
    if (typeof (SentryModule as any).startInactiveSpan !== "function") return () => {};
    // Do NOT use withIsolationScope — that strips user context from the new scope.
    // Call startInactiveSpan directly so the current scope's user is inherited.
    const span = (SentryModule as any).startInactiveSpan({
      name: screenName,
      op: "ui.screen",
      forceTransaction: true,
    });
    if (!span) return () => {};
    return () => {
      try {
        span.end();
      } catch {
        // never throw from monitoring
      }
    };
  } catch {
    return () => {};
  }
}

export async function initSentryFromFeatureFlags({ featureFlags }: { featureFlags: Record<string, any> }): Promise<void> {
  if (!isFeatureEnabled(featureFlags, sentryMonitoringFeature)) {
    return;
  }
  await initMonitoring({ featureFlags });
}

export function getSentry(): SentryModuleType | null {
  return SentryModule;
}

/**
 * First-party structured logging.
 *
 * This used to be a compatibility shim that called addBreadcrumb under log method names, so
 * every caller believing it wrote a log was writing a breadcrumb - visible only if an error
 * happened to follow, and never queryable. These now emit real Sentry logs.
 *
 * Why attributes rather than an interpolated message: consoleLoggingIntegration attaches
 * exactly one attribute (sentry.origin) and folds everything else into the message string, so
 * console-sourced logs can only ever be substring-matched. Attributes passed here become
 * indexed fields, which is what makes "group logouts by source and account" a query instead of
 * an afternoon. Keep the message a stable literal and put the variables in attributes - a
 * message built by interpolation is a message you cannot group by.
 */
function emitLog(level: "debug" | "info" | "warn" | "error", message: string, attributes?: LogAttributes): void {
  if (!isInitialized || !isEnabled || !SentryModule?.logger) return;
  try {
    SentryModule.logger[level](message, attributes);
  } catch {
    // never throw from monitoring
  }
}

export const logger = {
  debug: (message: string, attributes?: LogAttributes) => emitLog("debug", message, attributes),
  info: (message: string, attributes?: LogAttributes) => emitLog("info", message, attributes),
  warn: (message: string, attributes?: LogAttributes) => emitLog("warn", message, attributes),
  error: (message: string, attributes?: LogAttributes) => emitLog("error", message, attributes),
};
